import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// Remove static import
// import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
  IconCollection,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';
import Slider from 'react-slick';
import { brandLabel, listingPhotoUrl } from '../../../util/listingAttributes';

// Cloudinary photos are capped to these widths: the slide is at most about 660px tall, shown at
// up to 2x density, and the dot thumbnails are small squares (BSC-4)
const SLIDE_PHOTO_WIDTH = 1200;
const THUMBNAIL_PHOTO_WIDTH = 160;
const STILL_IMAGE_TYPES = ['image/gif', 'webp'];

// What the photos show, for alt text: the brand and the listing title, the brand only when the
// title does not already start with it
const photoSubject = listing => {
  const title = (listing.attributes.title || '').trim();
  const brand = brandLabel(listing.attributes.publicData);
  return brand && !title.toLowerCase().startsWith(brand.toLowerCase())
    ? `${brand} ${title}`.trim()
    : title;
};

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
function SampleNextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div className={className} style={{ ...style, display: 'block' }} onClick={onClick}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="26"
        height="46"
        viewBox="0 0 26 46"
        fill="none"
      >
        <path
          d="M21.0232 22.7045L1.67351 3.42001C1.48443 3.2328 1.33502 3.00972 1.23413 2.76399C1.13324 2.51827 1.08292 2.25489 1.08615 1.98946C1.08938 1.72402 1.1461 1.46193 1.25295 1.21872C1.3598 0.975494 1.5146 0.756088 1.70818 0.573494C2.10414 0.19939 2.63107 -0.00629437 3.17699 0.000148634C3.7229 0.00659163 4.24478 0.224651 4.63172 0.607994L25.4114 21.3186C25.5994 21.5044 25.7482 21.7257 25.8493 21.9694C25.9504 22.2132 26.0016 22.4746 26 22.7383C25.9984 23.002 25.9439 23.2627 25.8399 23.5052C25.7359 23.7477 25.5843 23.9672 25.394 24.1507L3.52239 45.4134C3.13085 45.7897 2.60781 46 2.06351 46C1.51922 46 0.99618 45.7897 0.604634 45.4134C0.413318 45.2285 0.261228 45.0072 0.157371 44.7627C0.0535134 44.5183 -7.62527e-08 44.2555 -8.78549e-08 43.9901C-9.94571e-08 43.7247 0.0535134 43.462 0.157371 43.2175C0.261228 42.973 0.413318 42.7518 0.604634 42.5669L21.0232 22.7045Z"
          fill="#3065A1"
        />
      </svg>
    </div>
  );
}

function SamplePrevArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div className={className} style={{ ...style, display: 'block' }} onClick={onClick}>
      <svg
        style={{
          transform: 'rotate(178deg)',
        }}
        xmlns="http://www.w3.org/2000/svg"
        width="26"
        height="46"
        viewBox="0 0 26 46"
        fill="none"
      >
        <path
          d="M21.0232 22.7045L1.67351 3.42001C1.48443 3.2328 1.33502 3.00972 1.23413 2.76399C1.13324 2.51827 1.08292 2.25489 1.08615 1.98946C1.08938 1.72402 1.1461 1.46193 1.25295 1.21872C1.3598 0.975494 1.5146 0.756088 1.70818 0.573494C2.10414 0.19939 2.63107 -0.00629437 3.17699 0.000148634C3.7229 0.00659163 4.24478 0.224651 4.63172 0.607994L25.4114 21.3186C25.5994 21.5044 25.7482 21.7257 25.8493 21.9694C25.9504 22.2132 26.0016 22.4746 26 22.7383C25.9984 23.002 25.9439 23.2627 25.8399 23.5052C25.7359 23.7477 25.5843 23.9672 25.394 24.1507L3.52239 45.4134C3.13085 45.7897 2.60781 46 2.06351 46C1.51922 46 0.99618 45.7897 0.604634 45.4134C0.413318 45.2285 0.261228 45.0072 0.157371 44.7627C0.0535134 44.5183 -7.62527e-08 44.2555 -8.78549e-08 43.9901C-9.94571e-08 43.7247 0.0535134 43.462 0.157371 43.2175C0.261228 42.973 0.413318 42.7518 0.604634 42.5669L21.0232 22.7045Z"
          fill="#3065A1"
        />
      </svg>
    </div>
  );
}
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [ImageGallery, setImageGallery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    intl,
    rootClassName,
    className,
    images,
    imageVariants,
    thumbnailVariants,
    handleFavouriteItems,
    indexValue,
    listing,
    handleSocialShare,
    isOwnListing,
  } = props;
  const { listingVideoAndImagesUrl = [] } = listing.attributes.publicData || {};
  const thumbVariants = thumbnailVariants || imageVariants;
  const subject = photoSubject(listing);
  const photoAlt = (index, count) =>
    intl.formatMessage({ id: 'ListingImageGallery.photoAltText' }, { subject, index, count });
  const thumbnailAlt = (index, count) =>
    intl.formatMessage(
      { id: 'ListingImageGallery.photoThumbnailAltText' },
      { subject, index, count }
    );
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: photoAlt(i + 1, images.length),
      thumbAlt: thumbnailAlt(i + 1, images.length),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });

  // Listings with Cloudinary media use the slider, which renders on the server; only the older
  // Sharetribe-image gallery needs react-image-gallery, loaded in the browser.
  const usesSlider = listingVideoAndImagesUrl?.length > 0;

  useEffect(() => {
    if (usesSlider) {
      setIsLoading(false);
      return;
    }
    const loadImageGallery = async () => {
      try {
        const module = await import('react-image-gallery');
        setImageGallery(() => module.default);
      } catch (error) {
        console.error('Failed to load react-image-gallery:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadImageGallery();
  }, []);

  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={1}
        height={1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
          />
        </div>
      </AspectRatioWrapper>
    );
  };

  const mediaCount = listingVideoAndImagesUrl?.length || 0;
  const renderItems =
    listingVideoAndImagesUrl?.length &&
    listingVideoAndImagesUrl
      // ?.filter(e => e.fileName !== 'tag')
      .map((val, index) => {
        // The first slide is the main photo: fetched straight away. The rest wait until needed.
        const loadingProps =
          index === 0 ? { fetchpriority: 'high' } : { loading: 'lazy', decoding: 'async' };
        return (
          <div key={index}>
            {STILL_IMAGE_TYPES.includes(val?.type) ? (
              <img
                src={listingPhotoUrl(val?.url, SLIDE_PHOTO_WIDTH)}
                alt={photoAlt(index + 1, mediaCount)}
                {...loadingProps}
              />
            ) : (
              <video className={css.sliderVideo} controls loop autoPlay>
                <source src={val?.url} type="video/mp4" />
              </video>
            )}
          </div>
        );
      });
  const renderThumbInner = item => {
    return (
      <div>
        {listingVideoAndImagesUrl?.length ? (
          listingVideoAndImagesUrl
            // ?.filter(e => e.fileName !== 'tag')
            .map((val, index) => {
              return (
                <div key={index}>
                  {STILL_IMAGE_TYPES.includes(val.type) ? (
                    <img
                      src={listingPhotoUrl(val.url, THUMBNAIL_PHOTO_WIDTH)}
                      alt={thumbnailAlt(index + 1, mediaCount)}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <video width="118" height="118" controls loop autoPlay>
                      <source src={val.url} type="video/mp4" />
                    </video>
                  )}
                </div>
              );
            })
        ) : (
          <ResponsiveImage
            rootClassName={css.thumb}
            image={item.image}
            alt={item.thumbAlt}
            variants={thumbVariants}
          />
        )}
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <div className={css.imagesButtons}>
        {!isOwnListing ? (
          <>
            {indexValue > -1 ? (
              <span
                onClick={() => {
                  handleFavouriteItems();
                }}
              >
                <IconCollection icon="like" />
              </span>
            ) : (
              <span
                onClick={() => {
                  handleFavouriteItems();
                }}
              >
                <IconCollection icon="dislike" />
              </span>
            )}{' '}
          </>
        ) : null}
        <span
          onClick={() => {
            handleSocialShare();
          }}
          className={css.openFullscreen}
        >
          <IconCollection icon="share" />
          <FormattedMessage id="ListingImageGallery.share" />
        </span>
        {/* <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button> */}
      </div>
    );
  };

  if (items.length === 0 && listingVideoAndImagesUrl.length === 0) {
    const classes = classNames(rootClassName || css.noImage, className);
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    customPaging: function(i) {
      return (
        <div>
          {listingVideoAndImagesUrl?.length &&
            listingVideoAndImagesUrl
              // ?.filter(e => e.fileName !== 'tag')
              .map(
                (val, index) =>
                  i === index && (
                    <div key={index}>
                      {STILL_IMAGE_TYPES.includes(val?.type) ? (
                        <img
                          src={listingPhotoUrl(val?.url, THUMBNAIL_PHOTO_WIDTH)}
                          alt={thumbnailAlt(index + 1, mediaCount)}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <video className={css.sliderVideoThumbnail} controls loop autoPlay>
                          <source src={val?.url} type="video/mp4" />
                        </video>
                      )}
                    </div>
                  )
              )}
        </div>
      );
    },

    responsive: [
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const storeType = listing.attributes.publicData?.listingStore;

  if (!usesSlider && isLoading) {
    return <div className={css.loading}>Loading gallery...</div>;
  }

  if (!usesSlider && !ImageGallery) {
    return <div className={css.error}>Failed to load gallery</div>;
  }

  return (
    <>
      {listingVideoAndImagesUrl?.length ? (
        <div
          className={classNames(
            css.listingImagesSlider,
            storeType == 'sneakers'
              ? css.sneakerBorder
              : storeType == 'streetwear'
              ? css.streetwearBorder
              : storeType == 'vintage'
              ? css.vintageBorder
              : null
          )}
        >
          {renderFullscreenButton()}
          <Slider {...settings}>{renderItems}</Slider>
        </div>
      ) : (
        <ImageGallery
          additionalClass={classes}
          items={items}
          renderItem={renderItem}
          showNav={false}
          renderThumbInner={renderThumbInner}
          onScreenChange={onScreenChange}
          renderLeftNav={renderLeftNav}
          renderRightNav={renderRightNav}
          renderFullscreenButton={renderFullscreenButton}
          {...IMAGE_GALLERY_OPTIONS}
        />
      )}
    </>
  );
};

ListingImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
};

const { string, arrayOf } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
